/**
 * The workspace for one research subject.
 *
 * **A subject is a person a researcher decided to keep, not a search result.** The search
 * page finds candidates; this is where the research about one of them lives, which is the
 * distinction phase 3 introduced and this screen finally makes visible.
 *
 * Structured like `CaseWorkspace` deliberately — a nav over sub-routes, one per panel — so
 * every panel has an address a researcher can bookmark, share in a ticket, or return to
 * after a refresh. A workspace whose state lives only in component memory is a workspace
 * nobody can point at.
 *
 * **The status control is the only path to a decision.** A matcher proposes with
 * arithmetic anybody can re-add and its vocabulary stops at `possible_match`; a person
 * decides here and is named for it. That separation is ADR-0008's, and it is what lets
 * `confirmed_by_researcher` exist at all.
 */

import type { ReactElement } from "react";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { download, request } from "@/lib/api";
import { Badge, Button, EmptyState, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";
import { SubjectEvidencePanel } from "@/features/people/SubjectEvidencePanel";
import { SubjectInterestsPanel } from "@/features/people/SubjectInterestsPanel";
import { SubjectNotesPanel } from "@/features/people/SubjectNotesPanel";
import { SubjectProfilesPanel } from "@/features/people/SubjectProfilesPanel";
import { SubjectTimelinePanel } from "@/features/people/SubjectTimelinePanel";
import { SubjectCasesPanel } from "@/features/people/SubjectCasesPanel";
import { SubjectSuggestionsPanel } from "@/features/people/SubjectSuggestionsPanel";
import type { UpdateSubjectRequest } from '@/lib/schemas';

/** A subject as the list endpoint returns it. */
export interface SubjectRow {
  id: string;
  display_name: string;
  status: string;
  evidence_count: number;
  first_seen_at: string;
  last_researched_at: string;
  created_at: string;
}

/** The panels, in the order a researcher works through them. */
const TABS = [
  { path: "evidence", label: "Evidence" },
  { path: "interests", label: "Interests" },
  { path: "profiles", label: "Profiles" },
  { path: "timeline", label: "Timeline" },
  { path: "notes", label: "Notes" },
  // After the panels that establish who this is, because that is the order the work
  // happens in: the identifiers here are only offered once somebody has confirmed it.
  { path: "identifiers", label: "Identifiers" },
  // Last, and named for what it is. A related case shares no evidence with the research
  // above it; putting it beside the evidence panel would invite the opposite reading.
  { path: "cases", label: "Related cases" },
] as const;

/** How a status is worded and toned. `confirmed_by_researcher` is a person's decision. */
const STATUSES: Record<string, { label: string; tone: "ok" | "warn" | "muted" }> = {
  unreviewed: { label: "Unreviewed", tone: "muted" },
  possible_match: { label: "Possible match", tone: "warn" },
  likely_match: { label: "Likely match", tone: "warn" },
  confirmed_by_researcher: { label: "Confirmed by researcher", tone: "ok" },
  rejected: { label: "Rejected", tone: "muted" },
};

/** The statuses a researcher may record, in workflow order. */
const DECISIONS = [
  "unreviewed",
  "possible_match",
  "likely_match",
  "confirmed_by_researcher",
  "rejected",
] as const;

/**
 * The subject workspace.
 *
 * @returns The workspace, or the reason it cannot be shown.
 */
export function SubjectWorkspace(): ReactElement {
  const { subjectId = "" } = useParams();
  const canDecide = useCan("create_run_cases");
  // The same permission that gates issuing a report. Assembling everything held on a
  // named person is what that permission restrains, and an analyst who may not issue a
  // report may not produce this either.
  const canExport = useCan("approve_export_reports");

  // **The pages, not the snippets that found them.** A search result is a few hundred
  // characters an engine chose; the page behind it is the whole thing, and a term three
  // paragraphs down is what the snippet never showed. Social profiles are refused by the
  // API and reported back as refused rather than dropped, because a page missing from the
  // result with no reason is indistinguishable from one nobody noticed.
  const fetchPages = useMutation({
    mutationFn: () =>
      request<{ dispatched: unknown[]; refused: { url: string; reason: string }[] }>(
        `/research/subjects/${subjectId}:fetch-pages`,
        { method: "POST" },
      ),
  });
  const queryClient = useQueryClient();

  // **Read from the list rather than a by-id endpoint, because there is not one.** The API
  // publishes `GET /research/subjects` and `PATCH /research/subjects/{id}`; adding a
  // by-id read for this screen would be a backend change to suit a page, and the list is
  // already cached by the search screen that linked here. If the list grows past a page
  // this becomes worth revisiting — noted rather than pre-solved.
  const subjects = useQuery({
    queryKey: ["research-subjects"],
    queryFn: () => request<SubjectRow[]>("/research/subjects?limit=200"),
  });

  // **The decision, and the only path to it.** A status is set by a person through this
  // API and by nothing else — the matcher's vocabulary stops at `possible_match`, so
  // `confirmed_by_researcher` can only ever mean somebody said so (ADR-0008).
  // **A download has to say who it is.** This was an anchor pointing straight at the API,
  // and an anchor cannot carry the bearer token: the session holds it in memory, so a
  // browser navigation arrives unauthenticated and the endpoint answers 401 — correctly.
  // The reports and evidence controls were both written this way and both never worked;
  // `download` exists because of them, and this is the third instance of the same defect.
  const exportDossier = useMutation({
    mutationFn: () => {
      // Named from the cached row rather than from `subject`, which is computed below the
      // early returns and so is not in scope for a hook that must be declared above them.
      const row = (subjects.data ?? []).find((entry) => entry.id === subjectId);
      const name = (row?.display_name ?? subjectId).replace(/[^\w.-]+/g, "-");
      return download(`/research/subjects/${subjectId}/export`, `dossier-${name}.json`);
    },
  });

  const decide = useMutation({
    mutationFn: (status: string) =>
      request<SubjectRow, UpdateSubjectRequest>(`/research/subjects/${subjectId}`, {
        method: "PATCH",
        body: { status },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["research-subjects"] }),
  });

  if (subjects.isLoading) return <Loading label="Loading subject" />;
  if (subjects.error != null) return <ErrorState error={subjects.error} />;

  const subject = (subjects.data ?? []).find((row) => row.id === subjectId);
  if (subject == null) {
    // A bookmark to a subject somebody else erased is a normal thing to happen, not an
    // error to log. Say so plainly and offer the way back.
    return (
      <EmptyState>
        No saved subject with this identifier. It may have been erased, or belong to
        another tenant.
      </EmptyState>
    );
  }

  const status = STATUSES[subject.status] ?? { label: subject.status, tone: "muted" as const };

  return (
    <div className="space-y-4" data-testid="subject-workspace">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">{subject.display_name}</h1>
          <Badge tone={status.tone}>{status.label}</Badge>
          <span className="text-xs text-muted">
            {subject.evidence_count} attached piece(s) of evidence
          </span>
          {canExport && (
            <span className="ml-auto">
              <Button
                tone="secondary"
                data-testid="export-dossier"
                disabled={exportDossier.isPending}
                onClick={() => exportDossier.mutate()}
              >
                {exportDossier.isPending ? "Assembling…" : "Export dossier"}
              </Button>
            </span>
          )}
          {canDecide && (
            <Button
              tone="secondary"
              data-testid="fetch-pages"
              disabled={fetchPages.isPending}
              onClick={() => fetchPages.mutate()}
            >
              {fetchPages.isPending ? "Fetching…" : "Fetch attached pages"}
            </Button>
          )}
          {canDecide && (
            <label className="flex items-center gap-2 text-xs text-muted">
              Decision
              <select
                className="rounded border border-line bg-surface p-1"
                data-testid="subject-status"
                value={subject.status}
                disabled={decide.isPending}
                onChange={(event) => decide.mutate(event.target.value)}
              >
                {DECISIONS.map((value) => (
                  <option key={value} value={value}>
                    {(STATUSES[value] ?? { label: value }).label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {decide.error != null && <ErrorState error={decide.error} />}
        {/* The export gate refuses after approval as well as before it, so a failure here
            has a reason worth reading rather than a status worth guessing at. */}
        {exportDossier.error != null && <ErrorState error={exportDossier.error} />}
        {fetchPages.error != null && <ErrorState error={fetchPages.error} />}
        {fetchPages.data != null && (
          <div className="max-w-3xl space-y-1 text-xs" data-testid="fetch-pages-result">
            <p className="text-muted">
              Fetching {fetchPages.data.dispatched.length} page(s). What they say appears in
              the panels below as each site answers — nothing further to do here.
            </p>
            {fetchPages.data.refused.map((item) => (
              <p key={item.url} className="text-muted">
                <span className="font-mono">{item.url}</span> — {item.reason}
              </p>
            ))}
          </div>
        )}
        <p className="max-w-3xl text-xs text-muted">
          A subject is a person a researcher chose to keep. Nothing here concludes anything
          about them: the status is a decision somebody recorded, and every panel below
          traces back to the evidence it came from.
        </p>
        {canExport && (
          <p className="max-w-3xl text-xs text-muted">
            The dossier is researcher working material — your notes, unconfirmed proposals
            and the platform&apos;s own uncertainty. It is <strong>not</strong> a subject
            access response and must not be sent to the person it describes.
          </p>
        )}
      </header>

      <nav aria-label="Subject sections" className="flex gap-1 border-b border-line">
        {TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            className={({ isActive }) =>
              `flex items-center gap-2 border-b-2 px-3 py-2 text-sm ${
                isActive ? "border-accent text-accent" : "border-transparent text-muted"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to="evidence" replace />} />
        <Route path="evidence" element={<SubjectEvidencePanel subjectId={subject.id} />} />
        <Route path="interests" element={<SubjectInterestsPanel subjectId={subject.id} />} />
        <Route path="profiles" element={<SubjectProfilesPanel subjectId={subject.id} />} />
        <Route path="timeline" element={<SubjectTimelinePanel subjectId={subject.id} />} />
        <Route path="notes" element={<SubjectNotesPanel subjectId={subject.id} />} />
        <Route
          path="identifiers"
          element={<SubjectSuggestionsPanel subjectId={subject.id} />}
        />
        <Route path="cases" element={<SubjectCasesPanel subjectId={subject.id} />} />
      </Routes>
    </div>
  );
}
